/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import { useEffect, useState } from "react";
import { LayoutDashboard, LogIn, LogOut, Zap } from "lucide-react";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function SiteHeader() {
    const [auth, setAuth] = useState({ phase: "checking", user: null, workspaceToken: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data: user } = await api.get("/auth/me", { withCredentials: true });
                let workspaceToken = null;
                try {
                    const { data: ws } = await api.get("/auth/my-workspace", { withCredentials: true });
                    if (ws?.hasWorkspace) workspaceToken = ws.webhookToken;
                } catch (_) { /* no workspace yet */ }
                if (!cancelled) setAuth({ phase: "authed", user, workspaceToken });
            } catch (_) {
                if (!cancelled) setAuth({ phase: "anon", user: null, workspaceToken: null });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const startLogin = () => {
        sessionStorage.setItem(
            "speedtolead.postLoginRedirect",
            window.location.pathname + window.location.search
        );
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href =
            "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout", null, { withCredentials: true });
        } catch (_) { /* ignore */ }
        window.location.href = "/";
    };

    return (
        <header
            className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/60"
            role="banner"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
                <a
                    href="/"
                    data-testid="site-logo-link"
                    className="group inline-flex items-center gap-2"
                    aria-label="SpeedToLead home"
                >
                    <span className="grid h-8 w-8 place-items-center bg-primary text-primary-foreground">
                        <Zap className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="font-display text-lg font-bold tracking-tight">
                        SpeedToLead
                    </span>
                </a>
                <nav
                    className="flex items-center gap-3 md:gap-6"
                    aria-label="Primary"
                >
                    <a
                        href="#how"
                        data-testid="nav-how"
                        className="hidden text-sm text-muted-foreground hover:text-foreground md:inline"
                    >
                        How it works
                    </a>
                    <a
                        href="#diagnose"
                        data-testid="nav-diagnose"
                        className="hidden text-sm text-muted-foreground hover:text-foreground md:inline"
                    >
                        Speed Test
                    </a>

                    {auth.phase === "authed" && auth.workspaceToken && (
                        <a
                            href={`/secure-ops-hub-99x/${auth.workspaceToken}`}
                            data-testid="nav-dashboard-btn"
                            className="inline-flex items-center gap-2 bg-primary px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform hover:-translate-y-0.5"
                        >
                            <LayoutDashboard className="h-3.5 w-3.5" aria-hidden="true" />
                            Dashboard
                        </a>
                    )}

                    {auth.phase === "authed" && (
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="nav-logout-btn"
                            className="inline-flex items-center gap-2 border border-border px-3 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground transition-colors hover:border-destructive/50 hover:text-destructive"
                            aria-label="Sign out"
                        >
                            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    )}

                    {auth.phase === "anon" && (
                        <button
                            type="button"
                            onClick={startLogin}
                            data-testid="nav-login-btn"
                            className="inline-flex items-center gap-2 bg-primary px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform hover:-translate-y-0.5"
                        >
                            <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
                            Log in
                        </button>
                    )}
                </nav>
            </div>
        </header>
    );
}
